/**
 * CHART ANALYTICA — the analytical engine, from bar 0 to the bar the reader clicked.
 *
 * <h2>It AUDITS the chart; it does not re-derive it</h2>
 *
 * The bands come from `buildCycleBands`, the CTS points from the marks already drawn, the pivots
 * from `detectPivots` and the RSI from `computeRSI` — in every case the same function the chart
 * itself called. That is the load-bearing decision of the whole feature and this repository has
 * paid for the alternative twice: a study that computes its own levels AGREES WITH ITSELF
 * whatever the renderer did, so every number it prints is unfalsifiable by the picture beside it.
 *
 * <p>The direct consequence is that Chart Analytica can be INCOMPLETE, and says so. No cycle
 * drawn means no channel geometry, so six dimensions report `unavailable` with the reason and the
 * other nine still answer. That is better than inventing bands: a reader comparing the dashboard
 * against the candles would find levels on one and not the other.
 *
 * <h2>Scope is [0, t_click], inclusive, and nothing reads past it</h2>
 *
 * Sections are clipped at the clicked bar, forward-return horizons that would run past it mark
 * their signal UNRESOLVED rather than failed, and a Fibonacci swing still retracing is reported
 * open. Those three are the only places the boundary can leak, and each is handled where it
 * arises rather than by a filter at the end — a filter would silently drop the evidence that
 * something was truncated.
 *
 * <h2>Why a LOOP and not a Worker</h2>
 *
 * The brief allows either. This is five linear passes over the bars plus one pivot scan, and it
 * is MEASURED and REPORTED: every report carries `elapsedMs`, and the dashboard prints it. A
 * worker would have to serialise the bars, the bands, the CTS points and the RSI across the
 * boundary, which on a 8,500-bar series costs more than the arithmetic does — and it would put
 * `import.meta.url` into a build whose test runner cannot parse it. The existing double-rAF busy
 * indicator is what keeps the frame responsive while it runs, which is this codebase's own answer
 * to the same question.
 *
 * <p>If `elapsedMs` ever stops being small, that number is the evidence to move it — rather than
 * a worker built against a cost nobody measured.
 */

import { DEFAULT_PIVOT_STRENGTH } from './pivots';
import { sectionByBar, sectionsFrom } from './analyticaGeometry';
import { analyseBands } from './analyticaBands';
import { analyseCts, DEFAULT_DIVERGENCE_WINDOW, DEFAULT_HORIZON } from './analyticaCts';
import { analyseBars, analyseFibonacci, analyseVolume, scoreEdge } from './analyticaStructure';

/** The fifteen dimensions, in the order the brief lists them and the dashboard renders them. */
export const DIMENSIONS = [
  { id: 1, key: 'bars', label: 'Bar Analysis Summary', group: 'Structure' },
  { id: 2, key: 'excursion', label: 'PTA / PTB Excursion Depth', group: 'Channel' },
  { id: 3, key: 'equatorPenetrations', label: 'Equator Penetrations', group: 'Channel' },
  { id: 4, key: 'equatorReversions', label: 'Equator Reversions', group: 'Channel' },
  { id: 5, key: 'ctsUpside', label: 'CTS Upside Reversals by Zone', group: 'CTS' },
  { id: 6, key: 'ctsDownside', label: 'CTS Downside Reversals by Zone', group: 'CTS' },
  { id: 7, key: 'ctsDivergence', label: 'RSI Divergence CTS Reversals', group: 'CTS' },
  { id: 8, key: 'heightVsExcursion', label: 'Band Size & Excursion Correlation', group: 'Channel' },
  { id: 9, key: 'distanceVsReversion', label: 'Distance from Equator & Reversion', group: 'CTS' },
  { id: 10, key: 'fibonacci', label: 'Fibonacci Retracement Completion', group: 'Structure' },
  { id: 11, key: 'boundaryPenetrations', label: 'Boundary Penetrations b0–b3', group: 'Channel' },
  { id: 12, key: 'boundaryReversions', label: 'Boundary Reversions b0–b3', group: 'Channel' },
  { id: 13, key: 'ctsRsiSynthesis', label: 'CTS & RSI Correlation Insights', group: 'CTS' },
  { id: 14, key: 'volume', label: 'Volume Profile & Liquidity', group: 'Structure' },
  { id: 15, key: 'edge', label: 'Edge Score / Confluence Rating', group: 'Synthesis' },
];

/** Why a dimension could not answer. Stated, never left as an empty table. */
export const UNAVAILABLE = {
  NO_BANDS: 'No PTA or PTB cycle is drawn on this chart, so there is no channel geometry to '
    + 'measure against. Click PTA — Long Cycle or PTB — Short Cycle, then run this again.',
  NO_CTS: 'No Critical Timing Scan is on this chart, so there are no timing points to score. '
    + 'Run the CTS study, then run this again.',
  NO_RSI: 'RSI is not available for this series.',
  NO_VOLUME: 'This instrument reports no volume, so there is nothing to profile.',
};

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A monotonic clock where one exists, and a working fallback where it does not (jsdom). */
function now() {
  return (typeof performance !== 'undefined' && typeof performance.now === 'function')
    ? performance.now() : Date.now();
}

/**
 * Runs every dimension that can be run, and names the ones that cannot.
 *
 * @param {object} input
 * @param {string} input.symbol
 * @param {Array<string>} input.dates     the chart's REAL bars, no future padding
 * @param {Array<number[]>} input.ohlc    ECharts order: [open, close, low, high]
 * @param {Array<number>} input.volumes
 * @param {Array<number>} input.rsi
 * @param {number} input.upToIndex        t_click, INCLUSIVE
 * @param {Array<object>} input.bands     `[{category, bands: buildCycleBands output}]`
 * @param {Array<object>} input.ctsPoints `[{date, direction}]` read off the overlay
 * @returns {object} the report the dashboard renders and the overlay marks
 */
export function runChartAnalytica({
  symbol = null,
  dates = [],
  ohlc = [],
  volumes = [],
  rsi = [],
  upToIndex = null,
  bands = [],
  ctsPoints = [],
  horizon = DEFAULT_HORIZON,
  divergenceWindow = DEFAULT_DIVERGENCE_WINDOW,
  pivotStrength = DEFAULT_PIVOT_STRENGTH,
} = {}) {
  const started = now();
  const last = n(upToIndex);
  if (last === null || last < 1 || !Array.isArray(ohlc) || ohlc.length === 0) {
    return {
      ok: false,
      reason: 'Chart Analytica needs at least two bars up to the clicked bar.',
      symbol,
      upToIndex: last,
      elapsedMs: now() - started,
    };
  }
  const to = Math.min(last, ohlc.length - 1, Math.max(0, dates.length - 1));

  // ── the channel geometry, from the chart's own bands ──────────────────────────────────────
  const sections = [];
  (Array.isArray(bands) ? bands : []).forEach((group) => {
    if (!group || !Array.isArray(group.bands)) return;
    sections.push(...sectionsFrom(group.bands, to, group.category));
  });
  sections.sort((a, b) => a.from - b.from);
  const hasBands = sections.length > 0;

  // Which section governs each bar. Built ONCE and handed to both the band pass and the CTS
  // pass, so the two cannot disagree about which channel a bar was standing in.
  const byBar = sectionByBar(sections, to);

  const bandResult = hasBands ? analyseBands(sections, ohlc) : null;
  const hasCts = Array.isArray(ctsPoints) && ctsPoints.length > 0;
  const hasVolume = (Array.isArray(volumes) ? volumes : []).some(v => n(v) !== null && v > 0);
  const hasRsi = (Array.isArray(rsi) ? rsi : []).some(v => n(v) !== null);

  const ctsResult = hasCts ? analyseCts({
    points: ctsPoints, dates, ohlc, rsi, sectionByBar: byBar, upToIndex: to,
    horizon, divergenceWindow, pivotStrength,
  }) : null;

  const volumeResult = hasVolume ? analyseVolume({
    volumes,
    events: bandResult?.events || [],
    ctsRows: ctsResult?.rows || [],
    upToIndex: to,
  }) : null;

  const edgeResult = (ctsResult && ctsResult.rows.length > 0)
    ? scoreEdge({ ctsRows: ctsResult.rows, ctsAnalysis: ctsResult, volume: volumeResult })
    : null;

  const unavailable = {};
  if (!hasBands) {
    ['excursion', 'equatorPenetrations', 'equatorReversions', 'heightVsExcursion',
      'boundaryPenetrations', 'boundaryReversions'].forEach((k) => {
      unavailable[k] = UNAVAILABLE.NO_BANDS;
    });
  }
  if (!hasCts) {
    ['ctsUpside', 'ctsDownside', 'ctsDivergence', 'distanceVsReversion', 'ctsRsiSynthesis',
      'edge'].forEach((k) => { unavailable[k] = UNAVAILABLE.NO_CTS; });
  } else if (!hasRsi) {
    unavailable.ctsDivergence = UNAVAILABLE.NO_RSI;
  }
  if (!hasVolume) unavailable.volume = UNAVAILABLE.NO_VOLUME;

  return {
    ok: true,
    symbol,
    upToIndex: to,
    clickedDate: dates[to] || null,
    firstDate: dates[0] || null,
    scannedBars: to + 1,
    // What this scan was actually able to see. Printed at the head of the dashboard, because
    // every figure below it is conditional on these three and a reader comparing two runs needs
    // to know whether they differed in scope or in outcome.
    inputs: {
      bands: sections.length,
      bandCategories: [...new Set(sections.map(s => s.category))].filter(Boolean),
      ctsPoints: ctsResult ? ctsResult.total : 0,
      rsi: hasRsi,
      volume: hasVolume,
      horizon,
      pivotStrength,
    },
    unavailable,
    bars: analyseBars(dates, ohlc, volumes, to),
    band: bandResult,
    cts: ctsResult,
    fibonacci: analyseFibonacci(ohlc, to, pivotStrength),
    volume: volumeResult,
    edge: edgeResult,
    sections,
    elapsedMs: now() - started,
  };
}

/**
 * The one-line headline for a run, for the history list.
 *
 * <h2>It carries the INPUTS, not only the scope</h2>
 *
 * The scope alone is not enough, and that was found by running it twice. Two scans of the same
 * bar produce the same symbol, the same date and the same bar count — so the selector showed two
 * identical rows and a reader could not tell which was which, which defeats the point of keeping
 * both. What actually DIFFERED between those two runs was what was on the chart when each ran:
 * the first saw no CTS scan because it was still loading, and the second saw 356 points.
 *
 * <p>So the section and point counts are part of the label. They are also the first thing a
 * reader comparing two runs needs, because a difference there means the runs differ in SCOPE
 * rather than in outcome — which is the distinction the Overview tab's own inputs block exists
 * to make.
 */
export function runLabel(report) {
  if (!report) return '';
  const bars = report.scannedBars ? `${report.scannedBars.toLocaleString()} bars` : '';
  const sections = report.inputs ? `${report.inputs.bands} sec` : '';
  const cts = report.inputs ? `${report.inputs.ctsPoints} CTS` : '';
  return [report.symbol, report.clickedDate, bars, sections, cts].filter(Boolean).join(' · ');
}

export default runChartAnalytica;
